import { useState, useEffect } from "react";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Tag, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../lib/branding";
import { isPasswordValid, PASSWORD_MIN_LEN } from "../lib/passwordPolicy";
import PasswordStrengthChecklist from "../components/PasswordStrengthChecklist";

export default function Signup() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { loginWithToken, user, ready } = useAuth();
  const branding = useBranding();
  const [form, setForm] = useState({
    workspace_name: "",
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    promo_code: (sp.get("promo") || "").toUpperCase(),
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState(null); // {valid, trial_days, partner_name}
  const [promoInvalid, setPromoInvalid] = useState(false);
  const trialDays = promo?.trial_days || 14;

  // Live promo validation (debounced)
  useEffect(() => {
    const code = form.promo_code.trim();
    if (!code) { setPromo(null); setPromoInvalid(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/promo/validate/${encodeURIComponent(code)}`);
        if (data.valid) { setPromo(data); setPromoInvalid(false); }
        else { setPromo(null); setPromoInvalid(true); }
      } catch { setPromo(null); setPromoInvalid(true); }
    }, 350);
    return () => clearTimeout(t);
  }, [form.promo_code]);

  if (ready && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!isPasswordValid(form.password)) {
      setErr(`Password must be at least ${PASSWORD_MIN_LEN} characters and include a letter, a number, and a symbol.`);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        workspace_name: form.workspace_name.trim(),
        full_name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
        email: form.email.trim(),
        password: form.password,
      };
      if (form.promo_code.trim() && promo?.valid) {
        payload.promo_code = form.promo_code.trim().toUpperCase();
      }
      const { data } = await api.post("/auth/signup", payload);
      if (loginWithToken) {
        await loginWithToken(data.token);
      } else {
        localStorage.setItem("pragma_token", data.token);
      }
      navigate("/", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not create your workspace");
    }
    setLoading(false);
  };

  return (
    <div className="auth-split" data-testid="signup-shell">
      {/* ─── Left (white) — form ─── */}
      <div className="auth-split-left">
        <div className="auth-split-logo">
          <img
            src={branding.logo_data_url || "/pragma-login-logo-v4.png"}
            alt="Pragma"
            data-testid="signup-brand-logo"
          />
        </div>

        <div className="auth-split-form-wrap">
          <h1 className="auth-split-h1" data-testid="signup-heading">Create your workspace</h1>
          <p className="auth-split-sub" data-testid="signup-sub">Start your {trialDays}-day free trial. No card required.</p>

          <form onSubmit={submit} data-testid="signup-form">
            <input
              id="signup-workspace"
              type="text"
              value={form.workspace_name}
              onChange={(e) => setForm({ ...form, workspace_name: e.target.value })}
              className="auth-split-input"
              placeholder="Company Name"
              data-testid="signup-workspace"
              required
              aria-label="Company Name"
            />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <input
                id="signup-first-name"
                type="text"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="auth-split-input"
                placeholder="First name"
                data-testid="signup-first-name"
                required
                autoComplete="given-name"
                aria-label="First name"
              />
              <input
                id="signup-last-name"
                type="text"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="auth-split-input"
                placeholder="Last name"
                data-testid="signup-last-name"
                required
                autoComplete="family-name"
                aria-label="Last name"
              />
            </div>

            <input
              id="signup-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="auth-split-input"
              placeholder="Work email"
              data-testid="signup-email"
              required
              autoComplete="email"
              aria-label="Work email"
            />

            <input
              id="signup-password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="auth-split-input"
              placeholder="Password"
              data-testid="signup-password"
              required
              minLength={PASSWORD_MIN_LEN}
              autoComplete="new-password"
              aria-label="Password"
            />
            <PasswordStrengthChecklist password={form.password} testid="signup-password-rules" />

            {/* Promo code (optional) */}
            <div className="relative" data-testid="signup-promo-wrap">
              <div className="relative">
                <Tag
                  size={14}
                  style={{
                    position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                    color: "var(--pg-text-mute)", pointerEvents: "none",
                  }}
                />
                <input
                  type="text"
                  value={form.promo_code}
                  onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })}
                  className="auth-split-input"
                  style={{ paddingLeft: 38, paddingRight: form.promo_code ? 38 : 14, textTransform: "uppercase", letterSpacing: "0.05em" }}
                  placeholder="Promo code (optional)"
                  data-testid="signup-promo"
                  autoComplete="off"
                  spellCheck={false}
                  aria-label="Promo code"
                />
                {form.promo_code && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, promo_code: "" })}
                    aria-label="Clear promo"
                    data-testid="signup-promo-clear"
                    style={{
                      position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                      background: "transparent", border: "none", color: "var(--pg-text-mute)",
                      cursor: "pointer", padding: 4, borderRadius: 4,
                    }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              {promo?.valid && (
                <div
                  data-testid="signup-promo-valid"
                  style={{
                    marginTop: 8, padding: "10px 12px", borderRadius: 8,
                    background: "rgba(0,166,110,0.08)",
                    border: "1px solid rgba(0,166,110,0.25)",
                    fontSize: 12, color: "#00754C", fontWeight: 500,
                    display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  <CheckCircle2 size={14} />
                  <span><b>{promo.code}</b> unlocked — <b>{promo.trial_days} days</b> free trial{promo.partner_name ? ` via ${promo.partner_name}` : ""}</span>
                </div>
              )}
              {promoInvalid && (
                <div
                  data-testid="signup-promo-invalid"
                  style={{
                    marginTop: 8, padding: "10px 12px", borderRadius: 8,
                    background: "rgba(229,72,77,0.06)",
                    border: "1px solid rgba(229,72,77,0.2)",
                    fontSize: 12, color: "#B12E32", fontWeight: 500,
                  }}
                >
                  Not a valid code — you&apos;ll get the standard 14-day trial.
                </div>
              )}
            </div>

            {err && <div className="auth-split-error" data-testid="signup-error">{err}</div>}

            <div className="auth-split-actions" style={{ justifyContent: "flex-end" }}>
              <button
                type="submit"
                className="auth-split-submit"
                disabled={loading}
                data-testid="signup-submit"
              >
                {loading ? "Creating…" : `Start ${trialDays}-day free trial`}
                <ArrowRight strokeWidth={2.2} />
              </button>
            </div>
          </form>
        </div>

        <div className="auth-split-footer" data-testid="signup-login-footer">
          Already have an account?{" "}
          <Link to="/login" data-testid="signup-login-link">Sign in</Link>
        </div>
      </div>

      {/* ─── Right (dark) — trial marketing copy ─── */}
      <div className="auth-split-right" data-testid="signup-marketing">
        <h2 className="auth-split-right-h1">
          {trialDays} days,
          <br />
          <span className="accent">on the house.</span>
        </h2>
        <p className="auth-split-right-sub" data-testid="signup-trial-sub">
          {promo?.valid
            ? `Extended trial unlocked with ${promo.code}. Spin up your workspace in under a minute.`
            : "Spin up your workspace in under a minute."}
          <br />
          No credit card. Cancel any time.
        </p>
        <ul className="auth-split-right-feats" data-testid="signup-feats">
          <li><CheckCircle2 strokeWidth={2} /> Unlimited leads, deals & contacts</li>
          <li><CheckCircle2 strokeWidth={2} /> Multi-pipeline Kanban + custom fields</li>
          <li><CheckCircle2 strokeWidth={2} /> Add your whole team, all free in trial</li>
        </ul>
      </div>
    </div>
  );
}
